import React, { useState, useEffect } from "react";
import { Button, Tooltip, message, Spin } from "antd";
import { useSelector } from "react-redux";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleFilled,
  DragOutlined
} from "@ant-design/icons";
import GetCascadeChildrenOrderedApi from "../api/plans/GetCascadeChildrenOrderedApi";
import UpdateCascadePlanHierarchyApi from "../api/plans/UpdateCascadePlanHierarchyApi";
import { openNotificationSuccess, openNotificationError } from "../helpers/NotificationHelpers";

function CascadePlanHierarchyManager({ planId, onHierarchyChange }) {
  const [cascadeChildren, setCascadeChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  // Get performance cycle and position from Redux state
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  // Helper function to format target values
  const formatTarget = (target, scale, scaleUnit) => {
    if (!target) return 'Not set';
    
    const numericTarget = Number(target);
    
    if (scale?.name === 'Percentage') {
      return `${numericTarget}%`;
    } else if (scale?.name === 'Currency') {
      return `$${numericTarget.toLocaleString()}`;
    } else if (scale?.name === 'Number') {
      return numericTarget.toLocaleString();
    } else {
      // Default formatting with scale unit abbreviation
      const abbreviation = scaleUnit?.abbreviation || '';
      return `${numericTarget.toLocaleString()}${abbreviation ? ' ' + abbreviation : ''}`;
    }
  };

  // Helper function to format performance percentage with color
  const formatPerformanceWithColor = (performance) => {
    const perf = performance || 0;
    const roundedPerf = Math.round(perf);
    
    // Match the color logic from TopPlanElement.js
    let colorClass = '';
    if (roundedPerf >= 70) {
      colorClass = 'text-green-600 bg-green-100';
    } else if (roundedPerf >= 40) {
      colorClass = 'text-orange-600 bg-orange-100';
    } else {
      colorClass = 'text-red-600 bg-red-100';
    }
    
    return {
      text: `${roundedPerf}%`,
      colorClass
    };
  };

  useEffect(() => {
    if (planId && perfCycleState?.id && positionState?.id) {
      // Always load fresh data when modal opens or dependencies change
      loadCascadeChildren();
    }
  }, [planId, perfCycleState?.id, positionState?.id]);

  const loadCascadeChildren = async () => {
    setLoading(true);
    try {
      // 🔑 FIXED: Always fetch fresh data, no caching
      const response = await GetCascadeChildrenOrderedApi(
        planId, 
        perfCycleState?.id, 
        positionState?.id
      );
      
      if (response.message === "success" && response.data) {
        // 🔑 FIXED: Sort by hierarchyOrder to ensure correct display order
        const sortedData = response.data.sort((a, b) => {
          const orderA = a.hierarchyOrder ?? 999;
          const orderB = b.hierarchyOrder ?? 999;
          if (orderA !== orderB) return orderA - orderB;
          return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime();
        });
        setCascadeChildren(sortedData);
      } else {
        message.error("Failed to load cascade children");
      }
    } catch (error) {
      console.error("Error loading cascade children:", error);
      message.error("Failed to load cascade children");
    } finally {
      setLoading(false);
    }
  };

  const movePlanUp = async (planId, currentOrder) => {
    if (currentOrder === 0) return; // Already at top
    
    // Move current plan to currentOrder - 1
    await updatePlanHierarchy(planId, currentOrder - 1);
  };

  const movePlanDown = async (planId, currentOrder) => {
    const maxOrder = cascadeChildren.length - 1;
    if (currentOrder === maxOrder) return; // Already at bottom
    
    // Move current plan to currentOrder + 1
    await updatePlanHierarchy(planId, currentOrder + 1);
  };

  const updatePlanHierarchy = async (cascadePlanId, newOrder) => {
    setUpdating(true);
    try {
      const response = await UpdateCascadePlanHierarchyApi(cascadePlanId, newOrder);
      
      if (response.message === "success") {
        openNotificationSuccess("bottomRight", "Cascade plan order updated successfully");
        
        // 🔑 NEW: Reload hierarchy data
        await loadCascadeChildren();
        
        // 🔑 NEW: Notify parent components for real-time updates
        if (onHierarchyChange) {
          onHierarchyChange();
        }
        
        // 🔑 NEW: Emit custom event for global updates
        // planId is the PARENT plan ID, cascadePlanId is the cascade plan that was moved
        window.dispatchEvent(new CustomEvent('cascadeHierarchyChanged', {
          detail: { 
            parentPlanId: planId, // Parent plan ID (the one with cascade children)
            updatedPlanId: cascadePlanId // Cascade plan that was moved
          }
        }));
        
      } else {
        openNotificationError("bottomRight", response.message || "Failed to update cascade plan order");
      }
    } catch (error) {
      console.error("Error updating cascade plan order:", error);
      openNotificationError("bottomRight", "Failed to update cascade plan order");
    } finally {
      setUpdating(false);
    }
  };

  const initializeHierarchyOrders = async () => {
    setUpdating(true);
    try {
      const token = 'Bearer ' + localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}v1/plans/fix-hierarchy-duplicates`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
        }
      );

      const data = await response.json();
      
      if (data.message === "success") {
        openNotificationSuccess("bottomRight", `Initialized hierarchy orders for ${data.data.updated} plans`);
        
        // 🔑 NEW: Reload hierarchy data
        await loadCascadeChildren();
        
        // 🔑 NEW: Notify parent components for real-time updates
        if (onHierarchyChange) {
          onHierarchyChange();
        }
        
        // 🔑 NEW: Emit custom event for global updates
        window.dispatchEvent(new CustomEvent('cascadeHierarchyChanged', {
          detail: { 
            parentPlanId: planId, // Parent plan ID
            updatedPlanId: null, // All plans were updated
            action: 'initialize'
          }
        }));
        
      } else {
        openNotificationError("bottomRight", "Failed to initialize hierarchy orders");
      }
    } catch (error) {
      console.error("Error initializing hierarchy orders:", error);
      openNotificationError("bottomRight", "Failed to initialize hierarchy orders");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <Spin size="large" />
        <div className="mt-2 text-gray-500">Loading cascade plans...</div>
      </div>
    );
  }

  if (cascadeChildren.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <div className="text-lg mb-2">No cascade plans found</div>
        <div className="text-sm">Create cascade plans to manage their hierarchy</div>
      </div>
    );
  }

  return (
    <div className="cascade-hierarchy-manager">
      <div className="space-y-3">
        {cascadeChildren.map((plan, index) => (
          <div
            key={plan.planId}
            className={`flex items-center justify-between p-4 bg-white border-2 rounded-lg shadow-sm transition-all duration-200 ${
              updating ? 'opacity-50' : 'hover:shadow-md hover:border-blue-300'
            }`}
          >
            <div className="flex items-center space-x-4 flex-1">
              <div className="text-gray-400">
                <DragOutlined className="text-lg" />
              </div>

              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                  {(plan.hierarchyOrder ?? index) + 1} {plan.hierarchyOrder === null ? '(auto)' : ''}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-gray-900 text-base">
                      {plan.title || 'Untitled Plan'}
                    </span>
                    {(plan.finalized === 1 || plan.finalized === "1" || plan.finalized === true) && (
                      <CheckCircleFilled
                        style={{ color: '#10B981', fontSize: '16px' }}
                        title="Plan Finalized"
                      />
                    )}
                  </div>
                  
                  <div className="text-sm text-gray-600 mt-1 flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-700">Target :</span>
                      <span>{formatTarget(plan.target, plan.scale, plan.scaleUnit)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-700">Performance:</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${formatPerformanceWithColor(plan.performance).colorClass}`}>
                        {formatPerformanceWithColor(plan.performance).text}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Tooltip title="Move up in hierarchy">
                <Button
                  type="text"
                  size="large"
                  icon={<ArrowUpOutlined />}
                  disabled={(plan.hierarchyOrder ?? index) === 0 || updating}
                  onClick={() => movePlanUp(plan.planId, plan.hierarchyOrder ?? index)}
                  className="hover:bg-green-50 hover:text-green-600"
                />
              </Tooltip>

              <Tooltip title="Move down in hierarchy">
                <Button
                  type="text"
                  size="large"
                  icon={<ArrowDownOutlined />}
                  disabled={(plan.hierarchyOrder ?? index) === (cascadeChildren.length - 1) || updating}
                  onClick={() => movePlanDown(plan.planId, plan.hierarchyOrder ?? index)}
                  className="hover:bg-red-50 hover:text-red-600"
                />
              </Tooltip>
            </div>
          </div>
        ))}
      </div>

      {cascadeChildren.length > 0 && cascadeChildren.some(plan => plan.hierarchyOrder === null) && (
        <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <div className="text-sm text-yellow-800 mb-3">
            <strong>⚠️ Notice:</strong> Some cascade plans don't have hierarchy orders assigned. 
            Click the button below to initialize them.
          </div>
          <Button 
            type="primary" 
            size="small"
            onClick={initializeHierarchyOrders}
            loading={updating}
          >
            Initialize Hierarchy Orders
          </Button>
        </div>
      )}
    </div>
  );
}

export default CascadePlanHierarchyManager;